/**
 * Premium Shipping Protection Auto-Add System
 * Automatically manages shipping protection based on cart value
 */

(() => {
class ShippingProtection {
  constructor() {
    this.productId = 8484643209304;
    this.protectionEnabled = this.getProtectionState();
    this.reconcilePromise = null;
    this.cartCache = null; // Cache cart data to avoid redundant API calls
    this.cacheTimeout = 2000; // Cache valid for 2 seconds
    this.variantIds = [
      43238395150424, 43238395183192, 43238395215960, 43238395248728, 43238395281496,
      43238395314264, 43238395347032, 43238395379800, 43238395412568, 43238395445336,
      43238395478104, 43238395510872, 43238395543640, 43238395576408, 43238395609176,
      43238395641944, 43238395674712, 43238395707480, 43238395740248, 43238395773016,
      43238395805784, 43238395838552, 43238395871320, 43238395904088, 43238395936856,
      43238395969624, 43238396002392, 43238396035160, 43238396067928, 43238396100696,
      43238396133464, 43238396166232, 43238396199000, 43238396231768, 43238396264536,
      43238396297304, 43238396330072, 43238396362840, 43238396395608, 43238396428376,
      43238396461144, 43238396493912, 43238396526680, 43238396559448, 43238396592216,
      43238396624984, 43238396657752, 43238396690520, 43238396723288, 43238396756056,
      43238396788824, 43238396821592, 43238396854360, 43238396887128, 43238396919896,
      43238396952664, 43238396985432, 43238397018200, 43238397050968, 43238397083736,
      43238397116504, 43238397149272, 43238397182040, 43238397214808, 43238397247576,
      43238397280344, 43238397313112, 43238397345880, 43238397378648, 43238397411416,
      43238397444184, 43238397476952, 43238397509720, 43238397542488, 43238397575256,
      43238397608024, 43238397640792, 43238397673560, 43238397706328, 43238397739096,
      43238397771864, 43238397804632, 43238397837400, 43238397870168, 43238397902936,
      43238397935704, 43238397968472, 43238398001240, 43238398034008, 43238398066776,
      43238398099544, 43238398132312, 43238398165080, 43238398197848, 43238398230616,
      43238398263384, 43238398296152, 43238398328920, 43238398361688, 43238398787672
    ];
    
    this.init();
  }

  init() {
    // Only operate on cart page (allows locale-prefixed URLs, e.g. /fr/cart, /fr-ca/cart)
    if (!/^(\/[a-z]{2}(-[a-z]{2})?)?\/cart\/?$/i.test(window.location.pathname)) {
      return;
    }

    const initializeCartPage = () => {
      this.showProtectionSection();
      this.setupCheckboxHandler();
      this.updateProtectionDisplay();
      this.autoAddProtectionOnLoad();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeCartPage, { once: true });
    } else {
      initializeCartPage();
    }
  }

  getCartUrl(endpoint) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${endpoint}`;
  }

  async getCartData(useCache = true) {
    // Use cached data if available and recent
    if (useCache && this.cartCache && (Date.now() - this.cartCache.timestamp) < this.cacheTimeout) {
      return this.cartCache.data;
    }

    // Fetch fresh cart data
    const response = await fetch(this.getCartUrl('cart.js'), {
      credentials: 'same-origin'
    });
    if (!response.ok) {
      throw new Error(`Unable to load cart (${response.status})`);
    }
    const cart = await response.json();
    
    // Cache the result
    this.cartCache = {
      data: cart,
      timestamp: Date.now()
    };

    return cart;
  }

  async getCartSubtotal(useCache = true) {
    const cart = await this.getCartData(useCache);
    return this.getCartSubtotalFromCart(cart);
  }

  async getCurrentProtectionItem(useCache = true) {
    const cart = await this.getCartData(useCache);
    return this.getProtectionItems(cart)[0] || null;
  }

  getProtectionItems(cart) {
    return cart.items.filter(item => Number(item.product_id) === this.productId);
  }

  getCartSubtotalFromCart(cart) {
    return cart.items.reduce((subtotal, item) => {
      return Number(item.product_id) === this.productId
        ? subtotal
        : subtotal + item.line_price;
    }, 0);
  }

  calculateRequiredTier(subtotal) {
    // Each tier covers $1000, starting from $0
    // Tier 1: $0-1000 = $30, Tier 2: $1001-2000 = $60, etc.
    const tier = Math.min(Math.ceil(subtotal / 100000), 100); // Convert cents to dollars and divide by 1000
    return tier;
  }

  getVariantIdForTier(tier) {
    // Tiers are 1-indexed, array is 0-indexed
    return this.variantIds[tier - 1];
  }

  getProtectionState() {
    // Default to enabled (true) unless explicitly disabled
    return localStorage.getItem('shipping_protection_enabled') !== 'false';
  }

  setProtectionState(enabled) {
    this.protectionEnabled = enabled;
    localStorage.setItem('shipping_protection_enabled', enabled.toString());
  }

  getDesiredProtection(cart) {
    // localStorage can be changed by another tab after this instance initializes.
    this.protectionEnabled = this.getProtectionState();
    const subtotal = this.getCartSubtotalFromCart(cart);
    if (!this.protectionEnabled || subtotal <= 0) {
      return { tier: 0, variantId: null };
    }

    const tier = this.calculateRequiredTier(subtotal);
    return {
      tier,
      variantId: this.getVariantIdForTier(tier)
    };
  }

  isProtectionCorrect(cart, desiredVariantId) {
    const protectionItems = this.getProtectionItems(cart);
    if (desiredVariantId === null) {
      return protectionItems.length === 0;
    }

    return protectionItems.length === 1 &&
      Number(protectionItems[0].variant_id) === desiredVariantId &&
      protectionItems[0].quantity === 1;
  }

  buildProtectionUpdates(cart, desiredVariantId) {
    const updates = {};
    let keptDesiredItem = false;

    this.getProtectionItems(cart).forEach(item => {
      const isDesiredItem = desiredVariantId !== null &&
        Number(item.variant_id) === desiredVariantId &&
        !keptDesiredItem;

      updates[item.key] = isDesiredItem ? 1 : 0;
      if (isDesiredItem) keptDesiredItem = true;
    });

    // update.js sets an existing variant to exactly 1, or adds it at 1 if absent.
    // Unlike add.js, repeated or concurrent calls don't increment the quantity.
    if (desiredVariantId !== null && !keptDesiredItem) {
      updates[String(desiredVariantId)] = 1;
    }

    return updates;
  }

  async updateCartQuantities(updates) {
    const response = await fetch(this.getCartUrl('cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      throw new Error(`Unable to reconcile shipping protection (${response.status})`);
    }

    const cart = await response.json();
    this.cartCache = { data: cart, timestamp: Date.now() };
    return cart;
  }

  async reconcileProtectionInternal() {
    let cart = await this.getCartData(false);
    let changed = false;
    const initialProtectionCount = this.getProtectionItems(cart).length;

    // update.js returns the complete cart, so use that response for verification.
    // A second update is only needed if a concurrent change left it incorrect.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const desired = this.getDesiredProtection(cart);
      if (this.isProtectionCorrect(cart, desired.variantId)) {
        return { ok: true, changed, initialProtectionCount, cart };
      }

      const updates = this.buildProtectionUpdates(cart, desired.variantId);
      cart = await this.updateCartQuantities(updates);
      changed = true;
    }

    const desired = this.getDesiredProtection(cart);
    return {
      ok: this.isProtectionCorrect(cart, desired.variantId),
      changed,
      initialProtectionCount,
      cart
    };
  }

  setBusy(busy) {
    this.busy = busy;
    document.dispatchEvent(new Event('shipping-protection:state'));
  }

  showError(visible) {
    const error = document.getElementById('shipping-protection-error');
    if (error) error.hidden = !visible;
  }

  async refreshDisplay(cart) {
    cart = cart || await this.getCartData(false);
    await this.updateProtectionDisplay(cart);
    if (typeof window.refreshCartPricingUI === 'function') {
      await window.refreshCartPricingUI(cart);
    }
  }

  async reconcileProtection() {
    if (this.reconcilePromise) {
      return this.reconcilePromise;
    }

    this.setBusy(true);
    this.reconcilePromise = this.reconcileProtectionInternal().catch(error => {
      console.error('Shipping protection safeguard failed:', error);
      return { ok: false, changed: false, initialProtectionCount: 0 };
    });

    try {
      return await this.reconcilePromise;
    } finally {
      this.reconcilePromise = null;
      this.setBusy(false);
    }
  }

  // Public cart-drawer integration used after AJAX add, quantity, and remove
  // operations. Keep the existing CA reconciliation logic and product IDs.
  async updateProtection() {
    return this.reconcileProtection();
  }

  async ensureProtectionForCheckout() {
    if (this.togglePromise) await this.togglePromise;
    // If an on-load reconciliation is running, let it finish before making the
    // checkout-specific fresh pass.
    if (this.reconcilePromise) {
      await this.reconcilePromise;
    }

    return (await this.reconcileProtection()).ok;
  }

  setupCheckboxHandler() {
    const checkbox = document.getElementById('shipping-protection-toggle');
    if (checkbox) {
      checkbox.checked = this.protectionEnabled;
      checkbox.addEventListener('change', (event) => {
        this.showError(false);
        this.setProtectionState(event.target.checked);
        this.toggleVersion = (this.toggleVersion || 0) + 1;
        this.optimisticPending = true;
        window.previewShippingProtection?.();
        if (!this.togglePromise) {
          this.togglePromise = this.syncToggle().finally(() => {
            this.togglePromise = null;
          });
        }
      });
    }
  }

  async syncToggle() {
    let version;
    do {
      version = this.toggleVersion;
      const result = await this.reconcileProtection();
      if (version !== this.toggleVersion) continue;
      try {
        let cart = result.cart;
        if (!result.ok) {
          cart = await this.getCartData(false);
          if (version !== this.toggleVersion) continue;
          this.setProtectionState(this.getProtectionItems(cart).length > 0);
          const checkbox = document.getElementById('shipping-protection-toggle');
          if (checkbox) checkbox.checked = this.protectionEnabled;
        }
        this.optimisticPending = false;
        await this.refreshDisplay(cart);
        this.showError(!result.ok);
      } catch (error) {
        if (version === this.toggleVersion) {
          this.optimisticPending = false;
          window.restoreShippingProtection?.();
          this.showError(true);
        }
      }
    } while (version !== this.toggleVersion);
  }

  showProtectionSection() {
    const section = document.getElementById('shipping-protection-section');
    if (section) {
      section.style.display = 'block';
    }
  }


  async updateProtectionDisplay(cart) {
  cart = cart || await this.getCartData();
  const subtotal = this.getCartSubtotalFromCart(cart);
  const tier = this.calculateRequiredTier(subtotal);

  const coveredValueDollars = tier * 1000; // <-- key change: use tier coverage blocks

  let price;
  if (tier <= 10) {
    price = coveredValueDollars * 0.03; // 3% of covered value
  } else if (tier <= 15) {
    price = 300; // flat
  } else {
    price = coveredValueDollars * 0.02; // 2% of covered value
  }

  // Optional: round to whole dollars for display
  const displayPrice = Math.round(price);

  const priceDisplay = document.getElementById('protection-price-display');
  if (priceDisplay) {
    const costLabel = priceDisplay.dataset.costLabel || 'Cost:';
    priceDisplay.textContent = `${costLabel} ${this.formatMoney(displayPrice * 100)}`;
  }

  await this.updateFallbackProtectionDisplay(cart);
}


  async updateFallbackProtectionDisplay(cart) {
    cart = cart || await this.getCartData();
    const subtotal = this.getCartSubtotalFromCart(cart);
    const currentProtection = this.getProtectionItems(cart)[0];
    const fallbackElement = document.getElementById('protection-price-fallback');
    const priceAmountElement = document.getElementById('protection-price-amount');
    
    if (fallbackElement && priceAmountElement) {
      if (this.protectionEnabled && currentProtection && currentProtection.line_price > 0 && subtotal > 0) {
        // Show fallback with current protection price
        const priceFormatted = this.formatMoney(currentProtection.line_price);
        priceAmountElement.textContent = priceFormatted + (this.isFrenchPrice() ? '' : ' CAD');
        fallbackElement.style.display = 'flex';
      } else {
        // Hide fallback
        fallbackElement.style.display = 'none';
      }
    }
  }

  isFrenchPrice() {
    const priceDisplay = document.getElementById('protection-price-display');
    const cartForm = document.getElementById('cart-form');
    const pageLanguage = document.documentElement.lang.toLowerCase();
    return priceDisplay?.dataset.priceIsFrench === 'true'
      || cartForm?.dataset.priceIsFrench === 'true'
      || pageLanguage === 'fr'
      || pageLanguage.startsWith('fr-');
  }

  formatMoney(cents) {
    if (this.isFrenchPrice()) {
      const priceDisplay = document.getElementById('protection-price-display');
      const cartForm = document.getElementById('cart-form');
      const amount = (Number(cents || 0) / 100)
        .toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        .replace(/,/g, '\u00a0')
        .replace('.', ',');
      const symbol = priceDisplay?.dataset.priceCurrencySymbol || cartForm?.dataset.priceCurrencySymbol || '$';
      const currencyCode = priceDisplay?.dataset.priceCurrencyCode || cartForm?.dataset.priceCurrencyCode;
      return `${amount}\u00a0${symbol}${currencyCode === 'CAD' ? 'CA' : ''}`;
    }
    const dollars = cents / 100;
    return `$${dollars.toFixed(2)}`;
  }

  async autoAddProtectionOnLoad() {
    const result = await this.reconcileProtection();
    if (!result.ok || !result.changed) return;

    try {
      await this.refreshDisplay(result.cart);
    } catch (error) {
      this.showError(true);
    }
  }

}

// Initialize shipping protection
if (!window.shippingProtection) {
  window.shippingProtection = new ShippingProtection();
}
})();
